/**
 * CompanyFormDialog Component
 * 
 * Modal dialog for creating/editing companies
 * Includes validation and form handling
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import type { Company, CreateCompanyRequest, UpdateCompanyRequest, CompanyStatus } from '@/types';

const companySchema = z.object({
  name: z.string().min(2, 'Company name must be at least 2 characters'),
  code: z
    .string()
    .min(2, 'Company code must be at least 2 characters')
    .max(20, 'Company code must be at most 20 characters')
    .regex(/^[A-Z0-9-]+$/, 'Company code must be uppercase letters, numbers, and hyphens only'),
  description: z.string().optional(),
  status: z.enum(['active', 'inactive', 'setup']),
});

type CompanyFormData = z.infer<typeof companySchema>;

interface CompanyFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company?: Company | null;
  onSubmit: (data: CreateCompanyRequest | UpdateCompanyRequest) => Promise<void>;
  onValidateCode?: (code: string, excludeId?: string) => Promise<boolean>;
}

export function CompanyFormDialog({
  open,
  onOpenChange,
  company,
  onSubmit,
  onValidateCode,
}: CompanyFormDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!company;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    setError,
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: '',
      code: '',
      description: '',
      status: 'setup',
    },
  });

  const statusValue = watch('status');

  // Reset form when dialog opens/closes or company changes
  useEffect(() => {
    if (open) {
      if (company) {
        reset({
          name: company.name,
          code: company.code,
          description: company.description || '',
          status: company.status,
        });
      } else {
        reset({
          name: '',
          code: '',
          description: '',
          status: 'setup',
        });
      }
    }
  }, [open, company, reset]);

  const handleFormSubmit = async (data: CompanyFormData) => {
    setIsSubmitting(true);

    try {
      // Validate company code uniqueness if provided
      if (onValidateCode && data.code !== company?.code) {
        const isAvailable = await onValidateCode(data.code, company?.id);
        if (!isAvailable) {
          setError('code', {
            type: 'manual',
            message: 'This company code is already in use',
          });
          setIsSubmitting(false);
          return;
        }
      }

      await onSubmit(data);
      toast.success(isEditing ? 'Company updated successfully' : 'Company created successfully');
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting company form:', error);
      toast.error(
        isEditing ? 'Failed to update company' : 'Failed to create company'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Company' : 'Create New Company'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update company details and settings'
              : 'Add a new company to your organization'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          <div className="space-y-4">
            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Company Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                placeholder="e.g., UK Operations"
                {...register('name')}
                disabled={isSubmitting}
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            {/* Company Code */}
            <div className="space-y-2">
              <Label htmlFor="code">
                Company Code <span className="text-red-500">*</span>
              </Label>
              <Input
                id="code"
                placeholder="e.g., UK-OPS"
                {...register('code')}
                disabled={isSubmitting}
                onChange={(e) => {
                  // Auto-uppercase
                  const value = e.target.value.toUpperCase();
                  setValue('code', value);
                }}
              />
              <p className="text-xs text-muted-foreground">
                Unique identifier (uppercase letters, numbers, and hyphens only)
              </p>
              {errors.code && (
                <p className="text-sm text-red-500">{errors.code.message}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional description of this company"
                rows={3}
                {...register('description')}
                disabled={isSubmitting}
              />
              {errors.description && (
                <p className="text-sm text-red-500">{errors.description.message}</p>
              )}
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={statusValue}
                onValueChange={(value) => setValue('status', value as CompanyStatus)}
                disabled={isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="setup">Setup (Initial configuration)</SelectItem>
                  <SelectItem value="active">Active (Fully operational)</SelectItem>
                  <SelectItem value="inactive">Inactive (Temporarily disabled)</SelectItem>
                </SelectContent>
              </Select>
              {errors.status && (
                <p className="text-sm text-red-500">{errors.status.message}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditing ? 'Update Company' : 'Create Company'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
